import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    DoCheck,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { Observable, Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import { IDropdownSearcherItem, IDropdownSearcherOptions } from './dropdown-searcher.interfaces';

@Component({
    selector: 'dropdown-searcher',
    templateUrl: './dropdown-searcher.component.html',
    styleUrls: ['./dropdown-searcher.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DropdownSearcherComponent implements DoCheck, OnInit, OnChanges, OnDestroy {
    @Input('data') data: Observable<IDropdownSearcherItem[]>;
    @Input('options') customOptions?: IDropdownSearcherOptions = <IDropdownSearcherOptions>{};
    @Input('value') value?: IDropdownSearcherItem; // pass if you want to set value
    @Output('onSearch') onSearch: Subject<string> = new Subject<string>();
    @Output('onChange') onChange: Subject<IDropdownSearcherItem> = new Subject<IDropdownSearcherItem>();

    defaultOptions: IDropdownSearcherOptions = {
        placeholder: 'Select value',
        searcherPlaceholder: 'Search...',
        dropdownMaxHeight: '200px',
        debounce: 500,
        storeItem: false,
        minSearchPhraseLength: 0,
    };
    options: IDropdownSearcherOptions = <IDropdownSearcherOptions>{};
    elementID: string;
    searcher: FormControl = new FormControl();
    items: IDropdownSearcherItem[] = [];
    searchingIndicator: boolean = false;

    constructor(private changeDetectorRef: ChangeDetectorRef) {}

    ngDoCheck(): void {
        this.changeDetectorRef.detectChanges();
    }

    ngOnInit(): void {
        this.elementID = 'dropdown-searcher-' + new Date().getTime();
        this.options = Object.assign(this.defaultOptions, this.customOptions || {});

        if (!this.value) {
            this.value = { value: null, label: this.options.placeholder };
        }
        if (this.options.storeItem) {
            const storedItem = localStorage.getItem(this.options.storeItem + '');
            if (storedItem) {
                try {
                    this.value = JSON.parse(storedItem);
                } catch (e) {
                    // ignore json parsing error
                }
            }
        }

        // @TODO (Konrad) maybe remove it?
        this.searcher.valueChanges.pipe(debounceTime(this.options.debounce)).subscribe(
            (phrase: string) => {
                if (phrase.length > 0 && phrase.length < this.options.minSearchPhraseLength) {
                    this.searchingIndicator = true;
                    return;
                }
                this.onSearch.next(phrase);
                this.searchingIndicator = false;
            },
            () => {
                this.searchingIndicator = false;
            }
        );

        this.onSearch.next(this.options.defaultSearchPhrase);
        this.searchingIndicator = true;
    }

    ngOnChanges(): void {
        if (this.data && this.data instanceof Observable) {
            this.data.subscribe(
                (items: IDropdownSearcherItem[]) => {
                    this.items = items;
                    this.searchingIndicator = false;
                },
                () => {
                    this.items = [];
                    this.searchingIndicator = false;
                }
            );
        }
    }

    ngOnDestroy(): void {
        if (this.onSearch && this.onSearch instanceof Subject) {
            this.onSearch.unsubscribe();
        }
    }

    setValue(value: IDropdownSearcherItem): void {
        this.value = value;
        this.onChange.next(value);

        if (this.options.storeItem) {
            localStorage.setItem(this.options.storeItem + '', JSON.stringify(value));
        }
    }
}
